import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../../config/api';
import TeamsManager from '../../components/Admin/TeamsManager';
import CalendarManager from '../../components/Admin/CalendarManager';
import AdminPinModal from '../../components/AdminPinModal/AdminPinModal';
import ChangePinModal from '../../components/ChangePinModal/ChangePinModal';
import ConsentRequestModal from '../../components/ConsentRequestModal/ConsentRequestModal';
import FamilyPrivacyModal from '../../components/FamilyPrivacyModal/FamilyPrivacyModal';
import CoordinatorAttendanceManager from '../../components/Attendance/CoordinatorAttendanceManager';
import { FaLock, FaShieldAlt, FaUsers, FaCalendarAlt, FaClipboardCheck } from 'react-icons/fa';
import './DashboardCoordenador.css';

const DashboardCoordenador = () => {
    const navigate = useNavigate();
    const [coordinatorData, setCoordinatorData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('teams'); // 'teams', 'attendance', or 'calendar'
    const [isAlsoAtleta, setIsAlsoAtleta] = useState(false);
    const [isAlsoTreinador, setIsAlsoTreinador] = useState(false);
    const [isAlsoSocio, setIsAlsoSocio] = useState(false);
    const [isAlsoAdmin, setIsAlsoAdmin] = useState(false);
    const [isChangePinOpen, setIsChangePinOpen] = useState(false);

    // Linked users & switching state
    const [currentUserId, setCurrentUserId] = useState(() => parseInt(localStorage.getItem('userId')) || null);
    const [linkedUsers, setLinkedUsers] = useState(() => {
        try { return JSON.parse(localStorage.getItem('linkedUsers') || '[]'); } catch { return []; }
    });
    const [showAdminPinModal, setShowAdminPinModal] = useState(false);
    const [pendingSwitchUser, setPendingSwitchUser] = useState(null);
    const [isSwitchingUser, setIsSwitchingUser] = useState(false);
    const [switchingToName, setSwitchingToName] = useState('');

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const token = localStorage.getItem('token');
                const userId = localStorage.getItem('userId');
                if (!token || !userId) {
                    navigate('/login');
                    return;
                }

                // Fetch User & Coordinator specific data
                const response = await fetch(`${API_BASE_URL}/api/users/${userId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });

                if (response.ok) {
                    const data = await response.json();
                    setCoordinatorData(data);

                    const roleNames = data.roles ? data.roles.map(r => (r.name || r).toLowerCase()) : [];
                    const hasAdmin = roleNames.includes('admin');
                    const hasAtleta = data.hasAthleteProfile || roleNames.includes('atleta');
                    const hasTreinador = data.hasCoachProfile || roleNames.includes('treinador') || roleNames.includes('coach');
                    const hasCoordenador = data.hasCoordinatorProfile || roleNames.includes('coordenador') || roleNames.includes('coordinator');
                    const hasSocio = data.hasMemberProfile || roleNames.includes('socio');

                    setIsAlsoAdmin(hasAdmin);
                    setIsAlsoAtleta(hasAtleta);
                    setIsAlsoTreinador(hasTreinador);
                    setIsAlsoSocio(hasSocio && !hasAtleta && !hasTreinador);
                } else {
                    console.error('Falha ao carregar dados do coordenador');
                }
            } catch (error) {
                console.error('Error fetching coordinator data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [navigate, currentUserId]);

    const refreshLinkedUsers = async () => {
        try {
            const token = localStorage.getItem('token');
            const primaryUserId = localStorage.getItem('primaryUserId');
            if (!token) return;
            const res = await fetch(`${API_BASE_URL}/api/auth/linked-users?primaryUserId=${primaryUserId || ''}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'X-Primary-User-Id': primaryUserId || ''
                }
            });
            if (res.ok) {
                const data = await res.json();
                setLinkedUsers(data);
                if (data.length > 0) {
                    localStorage.setItem('linkedUsers', JSON.stringify(data));
                } else {
                    localStorage.removeItem('linkedUsers');
                }
            }
        } catch (err) {
            console.warn('Could not refresh linked users:', err);
        }
    };

    useEffect(() => {
        refreshLinkedUsers();
    }, []);

    const [showConsentModal, setShowConsentModal] = useState(false);
    const [targetConsentUser, setTargetConsentUser] = useState(null);
    const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false);

    const handleLinkedTabClick = async (lu) => {
        if (currentUserId === lu.id) return;

        if (lu.requiresConsent) {
            setTargetConsentUser(lu);
            setShowConsentModal(true);
            return;
        }

        const type = lu.dashboardType?.toLowerCase() || 'socio';
        if (type === 'admin' || lu.requiresPin) {
            setPendingSwitchUser(lu);
            setShowAdminPinModal(true);
            return;
        }

        executeUserSwitch(lu);
    };

    const executeUserSwitch = async (lu) => {
        setIsSwitchingUser(true);
        setSwitchingToName(`${lu.firstName} ${lu.lastName}`);
        try {
            const token = localStorage.getItem('token');
            const primaryUserId = localStorage.getItem('primaryUserId');
            const res = await fetch(`${API_BASE_URL}/api/auth/switch-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                    'X-Primary-User-Id': primaryUserId || ''
                },
                body: JSON.stringify({ targetUserId: lu.id, primaryUserId: primaryUserId ? parseInt(primaryUserId) : null })
            });

            if (res.ok) {
                const data = await res.json();
                localStorage.setItem('token', data.token);
                localStorage.setItem('userId', data.id.toString());
                localStorage.setItem('roles', JSON.stringify(data.roles));
                localStorage.setItem('userName', `${data.firstName} ${data.lastName}`);
                localStorage.setItem('userEmail', data.email);

                const dashboardRoutes = {
                    atleta: '/dashboard-atleta',
                    treinador: '/dashboard-treinador',
                    coordenador: '/dashboard-coordenador',
                    admin: '/dashboard-admin',
                    socio: '/dashboard-socio'
                };
                const type = lu.dashboardType?.toLowerCase() || 'socio';
                const route = dashboardRoutes[type] || '/dashboard-socio';

                navigate(route);
                window.location.reload();
            } else {
                const err = await res.json().catch(() => ({}));
                alert(err.message || 'Erro ao alterar perfil');
                setIsSwitchingUser(false);
            }
        } catch (err) {
            console.error('Error switching user:', err);
            setIsSwitchingUser(false);
        }
    };

    const handlePinSuccess = () => {
        setShowAdminPinModal(false);
        if (pendingSwitchUser) {
            executeUserSwitch(pendingSwitchUser);
            setPendingSwitchUser(null);
        }
    };

    if (loading) {
        return (
            <div className="dashboard-loading" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0f172a' }}>
                <div className="spinner"></div>
            </div>
        );
    }

    if (!coordinatorData) {
        return <div className="error-state" style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>Dados do coordenador não encontrados.</div>;
    }

    const coordinatorProfile = coordinatorData.coordinatorProfile || { sportId: null, sportName: 'Todas as Modalidades' };

    return (
        <div className="dashboard-coordenador dashboard-wrapper">
            <div className="dashboard-bg-decoration">
                <div className="blob blob-1"></div>
            </div>

            {/* Linked Accounts Tabs */}
            {linkedUsers.length > 1 && (
                <div className="athlete-tabs">
                    <div className="container athlete-tabs-container">
                        {linkedUsers.map((lu) => (
                            <button
                                key={lu.id}
                                onClick={() => handleLinkedTabClick(lu)}
                                className={`athlete-tab ${currentUserId === lu.id ? 'active' : ''} ${lu.requiresConsent ? 'is-blocked' : ''}`}
                                title={lu.requiresConsent ? 'Requer Autorização (18+ anos)' : lu.requiresPin ? 'Requer PIN (18+ anos)' : ''}
                            >
                                {lu.requiresConsent ? (
                                    <FaLock style={{ color: '#ef4444', marginRight: '6px' }} />
                                ) : lu.requiresPin ? (
                                    <FaLock style={{ color: '#eab308', marginRight: '6px' }} />
                                ) : null}
                                {`${lu.firstName} ${lu.lastName}`.trim() || `Conta ${lu.id}`}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Profile Hero Header */}
            <header className="profile-hero">
                <div className="container">
                    <div className="hero-content-wrapper">
                        <div className="profile-main-info">
                            <div className="avatar-wrapper" title="Foto de perfil">
                                <img
                                    src={coordinatorData.profilePhotoUrl
                                        ? `${API_BASE_URL}${coordinatorData.profilePhotoUrl}`
                                        : "https://images.unsplash.com/vector-1742875355318-00d715aec3e8?q=80&w=880"}
                                    alt={coordinatorData.fullName}
                                    className="profile-avatar-img"
                                    onError={(e) => { e.target.src = "https://images.unsplash.com/vector-1742875355318-00d715aec3e8?q=80&w=880"; }}
                                />
                                <div className="avatar-status-badge"></div>
                            </div>
                            <div className="profile-details-column">
                                <h1 className="hero-user-name">{coordinatorData.fullName}</h1>
                                <div className="hero-meta-row">
                                    <div className="hero-meta-item">
                                        <i className="fas fa-medal"></i>
                                        <span>Coordenador de {coordinatorProfile.sportName || 'Modalidade'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="hero-actions">
                            <button
                                className="role-switch-btn"
                                onClick={() => setIsChangePinOpen(true)}
                                title="Alterar PIN de Acesso"
                            >
                                <i className="fas fa-key"></i>
                                <span>Alterar PIN</span>
                            </button>
                            {isAlsoAdmin && (
                                <button className="role-switch-btn" onClick={() => navigate('/admin')} title="Ver Dashboard Admin">
                                    <i className="fas fa-shield-alt"></i>
                                    <span>Ver como Admin</span>
                                </button>
                            )}
                            {isAlsoAtleta && (
                                <button className="role-switch-btn" onClick={() => navigate('/dashboard-atleta')} title="Ver Dashboard Atleta">
                                    <i className="fas fa-running"></i>
                                    <span>Ver como Atleta</span>
                                </button>
                            )}
                            {isAlsoTreinador && (
                                <button className="role-switch-btn" onClick={() => navigate('/dashboard-treinador')} title="Ver Dashboard Treinador">
                                    <i className="fas fa-whistle"></i>
                                    <span>Ver como Treinador</span>
                                </button>
                            )}
                            {isAlsoSocio && (
                                <button className="role-switch-btn" onClick={() => navigate('/dashboard-socio')} title="Ver Dashboard Sócio">
                                    <i className="fas fa-id-card"></i>
                                    <span>Ver como Sócio</span>
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </header>


            {/* Main Section - Teams & Calendar Management */}
            <section className="dashboard-main">
                <div className="container">
                    <div className="coordenador-sub-tabs">
                        <button
                            className={`sub-tab-btn ${activeTab === 'teams' ? 'active' : ''}`}
                            onClick={() => setActiveTab('teams')}
                        >
                            <FaUsers /> Gestão de Equipas
                        </button>
                        <button
                            className={`sub-tab-btn ${activeTab === 'attendance' ? 'active' : ''}`}
                            onClick={() => setActiveTab('attendance')}
                        >
                            <FaClipboardCheck /> Presenças das Equipas
                        </button>
                        <button
                            className={`sub-tab-btn ${activeTab === 'calendar' ? 'active' : ''}`}
                            onClick={() => setActiveTab('calendar')}
                        >
                            <FaCalendarAlt /> Calendário de Eventos
                        </button>
                    </div>

                    <div className="teams-manager-container">
                        {activeTab === 'teams' && (
                            <TeamsManager
                                isCoordinator={true}
                                coordinatorSportId={coordinatorProfile.sportId}
                            />
                        )}
                        {activeTab === 'attendance' && (
                            <CoordinatorAttendanceManager
                                coordinatorSportId={coordinatorProfile.sportId}
                            />
                        )}
                        {activeTab === 'calendar' && (
                            <CalendarManager
                                isCoordinator={true}
                                coordinatorSportId={coordinatorProfile.sportId}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* PIN Verification Modal */}
            <AdminPinModal
                isOpen={showAdminPinModal}
                targetUserId={pendingSwitchUser?.id}
                onSuccess={handlePinSuccess}
                onClose={() => { setShowAdminPinModal(false); setPendingSwitchUser(null); }}
            />

            {/* Change PIN Modal */}
            <ChangePinModal
                isOpen={isChangePinOpen}
                onClose={() => setIsChangePinOpen(false)}
                userType="coordenador"
            />

            <ConsentRequestModal
                isOpen={showConsentModal}
                targetUser={targetConsentUser}
                onClose={() => setShowConsentModal(false)}
                onRequestSent={() => refreshLinkedUsers()}
            />

            <FamilyPrivacyModal
                isOpen={isPrivacyModalOpen}
                onClose={() => setIsPrivacyModalOpen(false)}
            />
        </div>
    );
};

export default DashboardCoordenador;
